'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

import { MaintenanceError } from '@eliadmin/core/src/api/errors';
import { MaintenanceMode } from '@eliadmin/core/src/components/error/maintenance/maintenance-mode';
import Link from '@eliadmin/core/src/components/link';
import { NotFoundCard } from '@eliadmin/core/src/components/nt/not-found/not-found-card';
import { NotFoundVisual } from '@eliadmin/core/src/components/nt/not-found/not-found-visual';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * The error page of the site (`app/[locale]/error.tsx`, no Figma): the 404 card (`NotFoundCard`) –
 * grey, radius 16, the rainbow visual with an exclamation mark in the place of „404“ (decoration
 * only – the heading says what happened) and the text column – with the core words „Niečo sa
 * pokazilo“ and „Došlo k chybe, skúste to za chvíľu znova.“, „Skúsiť znova“ (`retry` loads the page
 * again, not only the boundary) and the way home. Maintenance mode keeps the core page, every other
 * error is reported to Sentry like the one of `global-error.tsx` (a boundary below the layout does
 * not do it by itself).
 */
export function ErrorView({ error, retry }: { error: unknown; retry: () => void }) {
  const { t } = useTranslations();

  const maintenance = error instanceof MaintenanceError;
  useEffect(() => {
    if (!maintenance) Sentry.captureException(error);
  }, [error, maintenance]);

  if (maintenance) return <MaintenanceMode data={error.data} />;

  return (
    <NotFoundCard visual={<NotFoundVisual label="!" trimSpacing={false} />}>
      <div role="alert" className="flex flex-col gap-4">
        <h1 className="nt-h2 text-[#0D110D]">{t('text594')}</h1>
        <p className="nt-body-l text-[#363D36]">{t('text530')}</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button onClick={() => retry()} iconLeft="arrow-clockwise" className="max-sm:w-full">
          {t('text595')}
        </Button>
        <Button asChild variant="outline" iconLeft="house" className="max-sm:w-full">
          <Link href="/">{t('text517')}</Link>
        </Button>
      </div>
    </NotFoundCard>
  );
}
